<template>
  <article
    data-qa="analyses_lpu_set_card"
    class="lpu-set-card d-flex flex-column flex-sm-row align-center align-sm-start ga-4 pa-4 pa-sm-6 rounded-xl bg-gray-0 w-100 min-w-0"
  >
    <div class="lpu-set-card__logo-col">
      <LpuLogoPicture
        v-if="ff_update_lpu_logo"
        img-class="lpu-set-card__logo lpu-set-card__order-logo"
        data-qa="analyses_lpu_set_card_logo"
        contain
        :files="logoFiles"
        :alt="logoAlt || `Логотип ${name}`"
      />
      <v-img
        v-else
        data-qa="analyses_lpu_set_card_logo"
        :src="logoUrl"
        :alt="logoAlt || `Логотип ${name}`"
        class="lpu-set-card__logo lpu-set-card__order-logo"
        contain
      />

      <div class="lpu-set-card__order-meta d-flex flex-column align-center ga-4">
        <LpuCardRating
          :rating="rating"
          :reviews-count="reviewsCount"
          :reviews-href="reviewsHref"
          :hide-rating="true"
        />

        <div
          v-if="discount"
          data-qa="analyses_lpu_set_card_discount"
          class="lpu-set-card__discount d-inline-flex align-center py-1 px-2 rounded bg-success success--text text-caption"
        >
          Скидка {{ discount }}%
        </div>
      </div>
    </div>

    <div class="lpu-set-card__main text-center text-sm-left">
      <div class="lpu-set-card__head lpu-set-card__order-head d-flex flex-column align-center align-sm-start ga-1 min-w-0 w-100">
        <div class="d-flex align-center justify-center justify-sm-start gc-2">
          <v-icon
            color="primary"
            class="flex-shrink-0"
            size="20"
          >
            ui-icon-hospital-40
          </v-icon>
          <span
            data-qa="analyses_lpu_set_card_type"
            class="text-subtitle-2 text--text"
          >
            Сеть лабораторий
          </span>
        </div>

        <div class="lpu-set-card__name text-h6">
          <a
            data-qa="analyses_lpu_set_card_name"
            :href="href"
            class="text--text text-decoration-none"
          >
            {{ name }}
          </a>
          <LpuLicenseBadge
            v-if="allLpusHasLicense"
            qa="analyses_lpu_set_card_verified_badge"
            text="Лицензии всех филиалов проверены"
          />
        </div>
      </div>

      <div class="lpu-set-card__details lpu-set-card__order-details d-flex flex-column align-start ga-3 min-w-0 w-100 text-left">
        <div class="d-flex align-center gc-2">
          <span class="ui-icon-laba ui-icon-fz-medium icon-secondary--text flex-shrink-0" />
          <span
            data-qa="analyses_lpu_set_card_analyses_count"
            class="text-body-2 text--text"
          >
            {{ analysesCountText }}
          </span>
        </div>
        <!--  ToDo Убрать "d-none", когда будет готова карта    -->
        <div class="d-none align-center gc-2">
          <span class="ui-icon-clinic ui-icon-fz-medium primary--text flex-shrink-0" />
          <a
            data-qa="analyses_lpu_set_card_branches_link"
            :href="branchesHref"
            class="text-body-2 primary--text text-decoration-none"
          >
            {{ branchesCountText }}
          </a>
        </div>
      </div>

      <a
        data-qa="analyses_lpu_set_card_order_btn"
        :href="orderHref"
        class="lpu-set-card__order-btn lpu-set-card__order-cta d-inline-flex align-center justify-center gc-2 py-3 px-4 rounded text-decoration-none w-100 w-sm-auto primary bg-gray-0--text"
      >
        <v-icon
          color="white"
          class="flex-shrink-0"
          size="22"
        >
          ui-icon-card
        </v-icon>
        <span class="text-button">
          Заказать онлайн
        </span>
      </a>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import LpuCardRating from 'src/components/pages/[town-translit]/diagnostika/analiz/components/LpuCardRating.vue'
import LpuLicenseBadge from 'src/components/pages/[town-translit]/diagnostika/analiz/components/LpuLicenseBadge.vue'
import LpuLogoPicture from 'src/components/common/LpuLogoPicture/LpuLogoPicture.vue'
import useContextStore from 'src/store/useContextStore'
import type { CommonContextStore } from 'src/types/context'
import type { LpuLogoImageFile } from 'src/utils/getLpuAvatarUrl'
import {
  formatLpuCardAnalysesCountText,
  formatLpuCardBranchesCountText,
} from 'src/components/pages/[town-translit]/diagnostika/analiz/_functions/formatLpuCardTexts'

const props = withDefaults(defineProps<{
  name: string
  logoUrl: string
  logoFiles?: LpuLogoImageFile[] // eslint-disable-line vue/require-default-prop
  logoAlt?: string
  href: string
  reviewsHref: string
  branchesHref: string
  orderHref: string
  rating: number
  reviewsCount: number
  analysesCount: number
  laboratoriesCount: number
  townNamePred: string
  discount?: number | null
  allLpusHasLicense?: boolean
}>(), {
  logoAlt: '',
  discount: null,
  allLpusHasLicense: false,
})

const { context }: CommonContextStore = useContextStore()
const ff_update_lpu_logo = computed(() => Boolean(context.request.feature_flags.ff_update_lpu_logo))

const analysesCountText = computed(() => formatLpuCardAnalysesCountText(props.analysesCount))

const branchesCountText = computed(() => formatLpuCardBranchesCountText(
  props.laboratoriesCount,
  props.townNamePred,
))
</script>

<style lang="scss" scoped>
@import 'src/styles/variables';
@import 'src/styles/mixins';

.lpu-set-card {
  box-shadow:
    0 2px 24px 0 rgba(9, 42, 72, 0.08),
    0 0 2px 0 rgba(21, 40, 92, 0.06),
    0 1px 2px 0 rgba(5, 23, 40, 0.08);

  &__logo-col,
  &__main {
    display: contents;
  }

  &__logo-col {
    :deep(img) {
      width: 100%;
      max-width: 240px;
      height: 80px;
      object-fit: contain;
    }
  }

  &__order-logo {
    order: 1;
  }

  &__order-head {
    order: 2;
  }

  &__order-meta {
    order: 3;
  }

  &__order-details {
    order: 4;
  }

  &__order-cta {
    order: 5;
  }

  &__logo {
    width: 100%;
    max-width: 240px;
    height: 80px;
    object-fit: contain;
  }

  &__name {
    max-width: 100%;
  }

  &__order-btn {
    &:hover {
      opacity: 0.9;
    }
  }

  @include media-breakpoint-up(sm) {
    &__logo-col,
    &__main {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    &__logo-col {
      align-items: center;
      flex-shrink: 0;
      width: 100%;
      max-width: 240px;
    }

    &__main {
      align-items: flex-start;
      flex-grow: 1;
      min-width: 0;
      width: 100%;
    }

    &__order-logo,
    &__order-head,
    &__order-meta,
    &__order-details,
    &__order-cta {
      order: initial;
    }
  }
}
</style>
